import React from 'react';

export const REGISTRATION_DISMISS_REASONS = [
  'I’m still exploring',
  'I need to see more first',
  'I don’t want another account',
  'I have questions about my data',
  'Something else',
];

const HARD_GATE_FEATURES = [
  { image: '/journey-icons/product-analysis.webp', scale: 1.8, title: 'Analyse a product', detail: 'Check anything against your skin' },
  { image: '/journey-icons/skin-journal.webp', scale: 1.55, title: 'Skin Journal', detail: 'Track how your skin changes' },
  { image: '/journey-icons/routine.webp', scale: 1.85, title: 'My Routine', detail: 'Check conflicts and timing' },
];

export default function ContextualSaveInvitation({
  variant = 'soft',
  onCreateAccount,
  onSignIn,
  onDismiss,
}) {
  const isHardGate = variant === 'hard';
  return (
    <div className="fixed inset-0 z-[10000] flex items-end justify-center bg-[#1b1111]/35" role="presentation">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-invitation-title"
        className="relative max-h-[92vh] w-full max-w-[560px] overflow-y-auto rounded-t-[30px] bg-[#FFFDFC] px-6 pb-[max(24px,env(safe-area-inset-bottom))] pt-4 shadow-[0_-20px_60px_rgba(52,28,28,0.18)] sm:mb-6 sm:rounded-[30px]"
      >
        <div className="mx-auto mb-6 h-1.5 w-12 rounded-full bg-[#D8CEC4]" />
        {isHardGate && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Close"
            className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full text-[25px] font-light text-[#8C807A]"
          >
            ×
          </button>
        )}
        <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F3E5D9] text-[#7B4937]" aria-hidden="true">
          <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 3.75h9.75L19.5 7.5V20.25H6V3.75Z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 3.75v5.5h6v-5.5M9 20.25v-6.5h7.5v6.5" />
          </svg>
        </div>
        <p className="mb-2 text-[13px] font-semibold uppercase tracking-[0.16em] text-[#A96F54]">{isHardGate ? 'Save what Son:mi knows' : 'Don’t lose this'}</p>
        <h2 id="save-invitation-title" className="mb-3 font-['Cormorant_Garamond'] text-[35px] font-medium leading-[1.02] text-[#341C1C] sm:text-[38px]">
          {isHardGate ? 'Your skin changes. Don’t start from scratch.' : 'Save your profile'}
        </h2>
        <p className="mb-7 max-w-[470px] text-[16px] leading-6 text-[#6F625D]">
          {isHardGate
            ? 'Products, weather, stress and time all affect your skin. Save what Son:mi has learned today, so it knows what changed, what helped and how to help next time.'
            : 'Create a free account to keep what Son:mi has learned about you, save your results and pick up where you left off.'}
        </p>

        {isHardGate && (
          <div className="mb-6 space-y-2" aria-label="Features included with an account">
            {HARD_GATE_FEATURES.map((feature) => (
              <div key={feature.title} className="flex items-center gap-3 rounded-2xl border border-[#EFE6DF] bg-[#FAF7F3] px-4 py-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#F3E5D9]" aria-hidden="true">
                  <img className="h-full w-full object-cover" style={{ transform: `scale(${feature.scale})` }} src={feature.image} alt="" />
                </span>
                <span className="min-w-0">
                  <strong className="block text-[16px] text-[#341C1C]">{feature.title}</strong>
                  <small className="mt-0.5 block text-[14px] leading-5 text-[#786F69]">{feature.detail}</small>
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-3">
          <button
            type="button"
            onClick={onCreateAccount}
            className="w-full rounded-full bg-[#341C1C] px-5 py-4 text-[16px] font-semibold text-white transition-colors hover:bg-[#512C2C]"
          >
            Create free account
          </button>
          <button
            type="button"
            onClick={onSignIn}
            className="w-full rounded-full border border-[#CDBEB3] bg-white px-5 py-4 text-[16px] font-semibold text-[#341C1C] transition-colors hover:bg-[#FAF5F1]"
          >
            I already have an account · Sign in
          </button>
          {isHardGate ? (
            <p className="m-0 px-4 pt-1 text-center text-[14px] leading-5 text-[#8C807A]">You can close this and review your conversation.</p>
          ) : (
            <button
              type="button"
              onClick={onDismiss}
              className="w-full px-5 py-2 text-[14px] font-medium text-[#8C807A]"
            >
              Not now
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

export function RegistrationDismissReason({ onSelect, onSkip }) {
  return (
    <div className="fixed inset-0 z-[10001] flex items-end justify-center bg-[#1b1111]/35" role="presentation">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="registration-reason-title"
        className="w-full max-w-[560px] rounded-t-[30px] bg-[#FFFDFC] px-6 pb-[max(24px,env(safe-area-inset-bottom))] pt-4 shadow-[0_-20px_60px_rgba(52,28,28,0.18)] sm:mb-6 sm:rounded-[30px]"
      >
        <div className="mx-auto mb-6 h-1.5 w-12 rounded-full bg-[#D8CEC4]" />
        <p className="mb-2 text-[13px] font-semibold uppercase tracking-[0.16em] text-[#A96F54]">One quick question</p>
        <h2 id="registration-reason-title" className="mb-3 font-['Cormorant_Garamond'] text-[35px] font-medium leading-[1.02] text-[#341C1C]">
          What stopped you today?
        </h2>
        <p className="mb-5 text-[15px] leading-6 text-[#6F625D]">One tap helps us make Son:mi more useful.</p>
        <div className="flex flex-wrap gap-2.5">
          {REGISTRATION_DISMISS_REASONS.map((reason) => (
            <button
              key={reason}
              type="button"
              onClick={() => onSelect?.(reason)}
              className="rounded-full border border-[#D8CBC1] bg-white px-4 py-3 text-[14px] font-medium text-[#4F342A]"
            >
              {reason}
            </button>
          ))}
        </div>
        <button type="button" onClick={onSkip} className="mt-5 w-full px-5 py-2 text-[14px] font-medium text-[#9B8E87]">Skip</button>
      </section>
    </div>
  );
}
